import { ReviewRoute } from './confirm-review';
import { toConfirmRoutes, ConfirmRoutePayload } from './confirm-review';
import { DeliveryPoint, StopRow } from './plan-result';
import { LatLon } from './route-optimization.interface';
import { RouteRecalc } from './route-recalc';

/* ---------------- Lo que sale del gestor como DATO ----------------- *
 *
 * Existe para poder comparar el motor actual contra otro (Valhalla): eso pide una
 * secuencia con coordenadas y una referencia contra la cual medir, y ninguna vista
 * de pantalla entrega ninguna de las dos.
 *
 * Módulo puro y no un método del diálogo: la regla de qué números acompañan a qué
 * secuencia (ver `RouteExportRoute.engine`) es la única parte de esto que puede
 * hacer daño, y acá se puede fijar por test sin montar un componente.
 */

/** Los números del motor para UNA ruta. */
export interface RouteEngineTotals {
  distanceKm: number;
  durationMin: number;
}

/** Los números del motor para UNA parada. */
export interface StopEngineTimes {
  arrivalTime: string;
  departureTime: string;
  serviceMin: number;
  /** Viaje desde la parada anterior; en la primera, desde el almacén. */
  legDurationMin: number;
}

export interface RouteExportStop {
  sequence: number;
  /** `order_id` del plan, con su sufijo de división si lo trae. */
  orderId: string;
  serial?: string;
  name: string;
  commercialName?: string;
  address: string;
  territory?: string;
  /** `null` cuando el pedido no tiene coordenadas. Nunca un cero de relleno. */
  lat: number | null;
  lon: number | null;
  weightLb: number;
  weightKg: number;
  /**
   * `true` si el operador la INCORPORÓ a mano: un pedido que no entró al plan.
   *
   * El archivo existe para comparar motores, así que tiene que distinguir qué
   * paradas no salieron de ninguno. La ruta que la lleva sale como `edited` —una
   * parada de más nunca es el orden del motor— así que sus números ya se omiten;
   * esto dice CUÁL fue la que no se puede atribuir a nadie.
   */
  injected: boolean;
  /**
   * Los tiempos del motor, o `null` si la ruta fue editada.
   *
   * El motor calculó estos tiempos para SU secuencia. En un orden editado
   * pertenecen a otra, así que no se emiten: ver el comentario de `order`.
   */
  engine: StopEngineTimes | null;
}

export interface RouteExportRoute {
  subRouteId: string;
  label: string;
  territoryName?: string;
  /**
   * `'engine'` si la secuencia exportada es la que el motor calculó en el plan;
   * `'recalculated'` si el operador la editó y **después** le pidió al motor que
   * resolviera esa ruta con su contenido actual; `'edited'` si la cambió y nadie la
   * evaluó.
   *
   * Los tres describen mediciones distintas, y por eso son tres valores y no un
   * booleano: `'recalculated'` SÍ lleva números del motor, pero de otra corrida que
   * la del plan y sobre otro conjunto de paradas. Fundirlo con `'engine'` mezclaría
   * dos mediciones en el archivo que existe justamente para comparar.
   *
   * De esto depende que el archivo sirva o envenene la comparación. Con `'edited'`
   * los números del motor se omiten —acá y en cada parada— porque describen otra
   * secuencia: emitirlos haría medir el otro motor sobre la secuencia B contra este
   * sobre la secuencia A, y atribuir la diferencia al motor. El error no se ve en el
   * archivo, aparece en la conclusión.
   *
   * Es la misma regla que la pantalla ya aplica: el mapa de una ruta editada no
   * dibuja trayecto, y sus paradas no muestran hora de llegada.
   */
  order: 'engine' | 'edited' | 'recalculated';
  capacityLb: number;
  trips: number;
  /** Origen del recorrido. `null` si la oficina no tiene uno resoluble. */
  warehouse: LatLon | null;
  engine: RouteEngineTotals | null;
  stops: RouteExportStop[];
}

export interface RouteExport {
  loadDate: string;
  assumptions: {
    serviceMinPerStop: number;
    shift: { start: string; end: string };
  };
  /** Paradas exportadas sin coordenadas. Se cuenta para que no pase inadvertido. */
  stopsWithoutCoordinates: number;
  /** Lo que se persistiría al confirmar: ids y secuencia, por sub-ruta. */
  confirmPayload: ConfirmRoutePayload[];
  routes: RouteExportRoute[];
  /** Lo que el archivo NO puede afirmar, dicho en el archivo. */
  notes: string[];
}

export interface RouteExportContext {
  loadDate: string;
  serviceMinPerStop: number;
  shift: { start: string; end: string };
  /**
   * Las paradas ya resueltas del plan, por `orderId`. De acá salen las coordenadas
   * y los tiempos del motor: son los mismos datos que la lista y los pines ya
   * muestran, y resolverlos otra vez desde las órdenes sería una segunda copia de la
   * misma traducción.
   */
  stopByOrderId: Map<string, StopRow>;
  /**
   * Los pedidos que NO entraron al plan, por `orderId`: de acá salen las
   * coordenadas de una parada incorporada a mano.
   *
   * Va aparte de `stopByOrderId` y no fundido con él porque estos puntos **no
   * tienen tiempos del motor** —el motor nunca los ubicó— y el tipo lo dice. Sin
   * esta segunda fuente, una parada incorporada salía `lat: null` y se contaba
   * entre las que "no se pueden rutear", que es lo contrario del requisito que la
   * dejó entrar: solo se puede incorporar un pedido CON coordenada.
   */
  pendingByOrderId?: ReadonlyMap<string, DeliveryPoint>;
  /** Origen por sub-ruta. Ausente = no se pudo resolver. */
  warehouseBySubRouteId: Map<string, LatLon>;
  /**
   * Totales que el motor calculó, por sub-ruta.
   *
   * Vienen del llamador y no se leen de `ReviewRoute`: el gestor guarda solo lo que
   * necesita para editar y NO lleva distancia ni duración. Sacarlos de ahí con un
   * cast devolvía `null` en silencio para todas las rutas — o sea, un archivo sin
   * referencia contra la cual comparar, que es justo para lo que existe.
   */
  engineBySubRouteId: Map<string, RouteEngineTotals>;
  /**
   * El recorrido que el motor calculó para la secuencia VIGENTE de una ruta editada.
   *
   * El llamador entrega solo los resultados que siguen describiendo su ruta: acá no
   * se vuelve a chequear obsolescencia, porque hacerlo daría una segunda definición
   * de "al día" que puede divergir de la que la pantalla muestra. El síntoma sería
   * un archivo con tiempos que la pantalla no exhibe, o al revés.
   */
  recalcBySubRouteId?: ReadonlyMap<string, RouteRecalc>;
}

/**
 * Arma el archivo exportable de las rutas en revisión.
 *
 * El payload de confirmación y las rutas legibles salen de la MISMA pasada sobre
 * `routes`: dos recorridos podrían describir secuencias distintas, y acá divergir
 * significa entregar un archivo que no sabe qué midió.
 */
export function buildRouteExport(
  routes: readonly ReviewRoute[],
  context: RouteExportContext,
): RouteExport {
  const list = (routes ?? []).filter((r) => !!r);
  let stopsWithoutCoordinates = 0;
  let editedRoutes = 0;
  let recalculatedRoutes = 0;

  const exported: RouteExportRoute[] = list.map((route) => {
    // El recálculo de esta ruta, si su resultado sigue describiéndola. El llamador
    // ya filtró los obsoletos: acá un resultado presente es un resultado vigente.
    const recalc = context.recalcBySubRouteId?.get(route.subRouteId);
    const edited = route.custom && !recalc;
    if (edited) editedRoutes++;
    if (recalc) recalculatedRoutes++;

    const stops: RouteExportStop[] = route.stops.map((stop, i) => {
      // `planned` es el único que puede aportar tiempos del motor. El pendiente
      // aporta lo demás —coordenadas, peso, serial— y nada más, que es exactamente
      // lo que se sabe de un pedido que el motor nunca ubicó.
      const planned = context.stopByOrderId.get(stop.orderId);
      const point: DeliveryPoint | undefined =
        planned ?? context.pendingByOrderId?.get(stop.orderId);
      const lat = point?.lat ?? null;
      const lon = point?.lon ?? null;
      if (lat === null || lon === null) stopsWithoutCoordinates++;

      // Tres procedencias distintas y NINGUNA se mezcla: el recálculo describe la
      // secuencia vigente, el plan describe la suya, y una ruta editada sin evaluar
      // no tiene tiempos que nadie pueda atribuirle.
      const stopEngineTimes = (): StopEngineTimes | null => {
        const fromRecalc = recalc?.stops.get(stop.orderId);
        if (fromRecalc) {
          return {
            arrivalTime: fromRecalc.arrivalTime,
            departureTime: fromRecalc.departureTime,
            serviceMin: context.serviceMinPerStop,
            legDurationMin: fromRecalc.legDurationMin,
          };
        }
        if (edited || recalc || !planned) return null;
        return {
          arrivalTime: planned.arrivalTime,
          departureTime: planned.departureTime,
          serviceMin: planned.serviceMin,
          legDurationMin: planned.legDurationMin,
        };
      };

      return {
        sequence: i + 1,
        orderId: stop.orderId,
        serial: point?.serial,
        // El nombre y la dirección salen de la parada del gestor, que es la que el
        // operador está viendo; el punto del plan solo aporta lo que ella no lleva.
        name: stop.name,
        commercialName: point?.commercialName,
        address: stop.address,
        territory: point?.territory,
        lat,
        lon,
        weightLb: stop.weightLb,
        weightKg: point?.weightKg ?? 0,
        injected: !!stop.injected,
        engine: stopEngineTimes(),
      };
    });

    return {
      subRouteId: route.subRouteId,
      label: route.label,
      territoryName: route.territoryName,
      order: recalc ? 'recalculated' : edited ? 'edited' : 'engine',
      capacityLb: route.capacityLb,
      trips: route.trips,
      warehouse: context.warehouseBySubRouteId.get(route.subRouteId) ?? null,
      engine: recalc
        ? { distanceKm: recalc.totalDistanceKm, durationMin: recalc.totalDurationMin }
        : edited
          ? null
          : context.engineBySubRouteId.get(route.subRouteId) ?? null,
      stops,
    };
  });

  const notes: string[] = [];
  if (editedRoutes > 0) {
    notes.push(
      `${editedRoutes} ${editedRoutes === 1 ? 'ruta lleva' : 'rutas llevan'} un orden editado a ` +
        `mano: el motor no evaluó esa secuencia, así que sus distancias, duraciones y tiempos por ` +
        `parada se omiten. Las coordenadas y el orden sí son los vigentes.`,
    );
  }
  if (recalculatedRoutes > 0) {
    notes.push(
      `${recalculatedRoutes} ${recalculatedRoutes === 1 ? 'ruta fue recalculada' : 'rutas fueron recalculadas'} ` +
        `contra el motor sobre su secuencia vigente: sus distancias, duraciones y tiempos por parada ` +
        `son reales, pero NO son los de la corrida del plan — describen otro conjunto de paradas.`,
    );
  }
  if (stopsWithoutCoordinates > 0) {
    notes.push(
      `${stopsWithoutCoordinates} ${stopsWithoutCoordinates === 1 ? 'parada' : 'paradas'} sin ` +
        `coordenadas: salen en null y no se pueden rutear.`,
    );
  }

  return {
    loadDate: context.loadDate,
    assumptions: {
      serviceMinPerStop: context.serviceMinPerStop,
      shift: { ...context.shift },
    },
    stopsWithoutCoordinates,
    confirmPayload: toConfirmRoutes(list),
    routes: exported,
    notes,
  };
}
